import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Checkbox, CheckboxProps, Divider, Form, Message } from 'semantic-ui-react';
import { AgencyStateType } from '../../actions/actionTypes';
import { getAgeVerificationConfiguration,
    getMobileSecuritySettings,
    MobileSecurityApiError,
    saveAgeVerificationConfiguration,
    saveMobileSecuritySettings } from '../../actions/mobileSecuritySettings';
import { AppState } from '../../reducers';
import { getSelectedOrDefaultAgency } from '../../selectors';

const sectionStyle: React.CSSProperties = {
    width: '100%',
};

interface Props {
    agency: AgencyStateType | undefined;
}

const getPlatformEnabled = (platforms: { platform: string; enabled: boolean }[], platform: string) =>
    platforms.find((item) => item.platform === platform)?.enabled ?? true;

const MobileSecuritySettingsSection: React.FC<Props> = ({ agency }) => {
    const [pinningBlockingState, setPinningBlockingState] = useState(false);
    const [ageVerificationBlockingState, setAgeVerificationBlockingState] = useState(false);
    const [pinningEnabledState, setPinningEnabledState] = useState(false);
    const [pinningValidationEnabledState, setPinningValidationEnabledState] = useState(false);
    const [iosAgeVerificationEnabledState, setIosAgeVerificationEnabledState] = useState(true);
    const [androidAgeVerificationEnabledState, setAndroidAgeVerificationEnabledState] = useState(true);
    const [ageVerificationVersionState, setAgeVerificationVersionState] = useState(0);
    const [ageVerificationUpdatedAtState, setAgeVerificationUpdatedAtState] = useState<string | undefined>();
    const [pinningSuccessMessageState, setPinningSuccessMessageState] = useState('');
    const [pinningErrorMessageState, setPinningErrorMessageState] = useState('');
    const [ageVerificationSuccessMessageState, setAgeVerificationSuccessMessageState] = useState('');
    const [ageVerificationErrorMessageState, setAgeVerificationErrorMessageState] = useState('');

    const loadPinningSettings = useCallback(async (orgId: string) => {
        setPinningBlockingState(true);
        setPinningSuccessMessageState('');
        setPinningErrorMessageState('');

        try {
            const settings = await getMobileSecuritySettings(orgId);
            setPinningEnabledState(settings.pinningEnabled);
            setPinningValidationEnabledState(settings.pinningValidationEnabled);
        } catch {
            setPinningErrorMessageState('Failed to load certificate pinning settings.');
        } finally {
            setPinningBlockingState(false);
        }
    }, []);

    const loadAgeVerificationSettings = useCallback(async (orgId: string) => {
        setAgeVerificationBlockingState(true);
        setAgeVerificationSuccessMessageState('');
        setAgeVerificationErrorMessageState('');

        try {
            const settings = await getAgeVerificationConfiguration(orgId);
            setIosAgeVerificationEnabledState(getPlatformEnabled(settings.platforms, 'ios'));
            setAndroidAgeVerificationEnabledState(getPlatformEnabled(settings.platforms, 'android'));
            setAgeVerificationVersionState(settings.version);
            setAgeVerificationUpdatedAtState(settings.updatedAtUtc);
        } catch (error) {
            if (error instanceof MobileSecurityApiError && error.statusCode === 403) {
                setAgeVerificationErrorMessageState('You are not authorized to view age verification settings for this agency.');
            } else {
                setAgeVerificationErrorMessageState('Failed to load age verification settings.');
            }
        } finally {
            setAgeVerificationBlockingState(false);
        }
    }, []);

    useEffect(() => {
        if (!agency?.id) {
            return;
        }

        void loadPinningSettings(agency.id);
        void loadAgeVerificationSettings(agency.id);
    }, [agency?.id, loadAgeVerificationSettings, loadPinningSettings]);

    const onPinningValidationEnabledChange = useCallback((_event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
        const enabled = !!data.checked;
        setPinningValidationEnabledState(enabled);
        if (!enabled) {
            setPinningEnabledState(false);
        }
    }, []);

    const onPinningEnabledChange = useCallback((_event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
        if (!pinningValidationEnabledState && data.checked) {
            return;
        }

        setPinningEnabledState(!!data.checked);
    }, [pinningValidationEnabledState]);

    const isInvalidPolicy = useMemo(
        () => pinningValidationEnabledState === false && pinningEnabledState === true,
        [pinningEnabledState, pinningValidationEnabledState],
    );

    const onPinningSaveHandler = useCallback(async () => {
        if (!agency?.id) {
            return;
        }

        setPinningBlockingState(true);
        setPinningSuccessMessageState('');
        setPinningErrorMessageState('');

        try {
            await saveMobileSecuritySettings({
                orgId: agency.id,
                pinningEnabled: pinningEnabledState,
                pinningValidationEnabled: pinningValidationEnabledState,
            });
            setPinningSuccessMessageState('Certificate pinning settings saved successfully.');
        } catch {
            setPinningErrorMessageState('Failed to save certificate pinning settings.');
        } finally {
            setPinningBlockingState(false);
        }
    }, [agency?.id, pinningEnabledState, pinningValidationEnabledState]);

    const onAgeVerificationSaveHandler = useCallback(async () => {
        if (!agency?.id) {
            return;
        }

        setAgeVerificationBlockingState(true);
        setAgeVerificationSuccessMessageState('');
        setAgeVerificationErrorMessageState('');

        try {
            const saved = await saveAgeVerificationConfiguration({
                orgId: agency.id,
                version: ageVerificationVersionState,
                platforms: [
                    { platform: 'ios', enabled: iosAgeVerificationEnabledState },
                    { platform: 'android', enabled: androidAgeVerificationEnabledState },
                ],
            });
            setAgeVerificationVersionState(saved.version);
            setAgeVerificationUpdatedAtState(saved.updatedAtUtc);
            setAgeVerificationSuccessMessageState('Age verification settings saved successfully.');
        } catch (error) {
            if (error instanceof MobileSecurityApiError) {
                if (error.statusCode === 409) {
                    setAgeVerificationErrorMessageState(
                        'The mobile security configuration was changed by another administrator. Reloading the latest values.',
                    );
                    await loadAgeVerificationSettings(agency.id);
                } else if (error.statusCode === 403) {
                    setAgeVerificationErrorMessageState('You are not authorized to update age verification settings for this agency.');
                } else {
                    setAgeVerificationErrorMessageState(error.message || 'Failed to save age verification settings.');
                }
            } else {
                setAgeVerificationErrorMessageState('Failed to save age verification settings.');
            }
        } finally {
            setAgeVerificationBlockingState(false);
        }
    }, [
        agency?.id,
        ageVerificationVersionState,
        androidAgeVerificationEnabledState,
        iosAgeVerificationEnabledState,
        loadAgeVerificationSettings,
    ]);

    const agencyLabel = useMemo(() => agency?.name ?? agency?.id ?? 'Unknown agency', [agency]);

    const formattedUpdatedAt = useMemo(() => {
        if (!ageVerificationUpdatedAtState) {
            return 'Not saved yet';
        }

        return new Date(ageVerificationUpdatedAtState).toLocaleString();
    }, [ageVerificationUpdatedAtState]);

    return (
        <div className="mobileSecuritySection" style={sectionStyle}>
            <h3 style={{ width: '100%' }}>Mobile security</h3>
            <p>
                Settings for agency: <strong>{agencyLabel}</strong> ({agency?.id})
            </p>

            <Form>
                <h4>Certificate pinning</h4>
                {pinningSuccessMessageState && (
                    <Message positive content={pinningSuccessMessageState} />
                )}
                {pinningErrorMessageState && (
                    <Message negative content={pinningErrorMessageState} />
                )}
                <Form.Field>
                    <Checkbox
                        toggle
                        label="Certificate pinning enabled"
                        checked={pinningEnabledState}
                        disabled={pinningBlockingState || !pinningValidationEnabledState}
                        onChange={onPinningEnabledChange}
                    />
                    <p style={{ marginTop: '0.5em', color: '#666' }}>
                        When enabled, the mobile app enforces certificate pinning for API requests.
                    </p>
                </Form.Field>
                <Form.Field>
                    <Checkbox
                        toggle
                        label="Pinning validation enabled"
                        checked={pinningValidationEnabledState}
                        disabled={pinningBlockingState}
                        onChange={onPinningValidationEnabledChange}
                    />
                    <p style={{ marginTop: '0.5em', color: '#666' }}>
                        When enabled, pinning validation failures are treated as errors. When disabled, validation may be logged without blocking requests.
                    </p>
                </Form.Field>
                <Button
                    type="button"
                    primary
                    content="Save pinning"
                    loading={pinningBlockingState}
                    disabled={pinningBlockingState || isInvalidPolicy}
                    onClick={() => {
                        void onPinningSaveHandler();
                    }}
                    className="primaryButton"
                />
            </Form>

            <Divider horizontal section>
                <h4 style={{ margin: 0 }}>Age verification</h4>
            </Divider>

            <Form>
                {ageVerificationSuccessMessageState && (
                    <Message positive content={ageVerificationSuccessMessageState} />
                )}
                {ageVerificationErrorMessageState && (
                    <Message negative content={ageVerificationErrorMessageState} />
                )}
                <Form.Field>
                    <Checkbox
                        toggle
                        label="iOS age verification"
                        checked={iosAgeVerificationEnabledState}
                        disabled={ageVerificationBlockingState}
                        onChange={(_event, data) => setIosAgeVerificationEnabledState(!!data.checked)}
                    />
                </Form.Field>
                <Form.Field>
                    <Checkbox
                        toggle
                        label="Android age verification"
                        checked={androidAgeVerificationEnabledState}
                        disabled={ageVerificationBlockingState}
                        onChange={(_event, data) => setAndroidAgeVerificationEnabledState(!!data.checked)}
                    />
                </Form.Field>
                <p style={{ color: '#666' }}>
                    Version: {ageVerificationVersionState} | Last updated: {formattedUpdatedAt}
                </p>
                <Button
                    type="button"
                    primary
                    content="Save age verification"
                    loading={ageVerificationBlockingState}
                    disabled={ageVerificationBlockingState}
                    onClick={() => {
                        void onAgeVerificationSaveHandler();
                    }}
                    className="primaryButton"
                />
            </Form>
        </div>
    );
};

export default connect(
    (state: AppState) => ({
        agency: getSelectedOrDefaultAgency(state),
    }),
)(MobileSecuritySettingsSection);
